import { useCallback, useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Combobox, DropdownButton } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { SELECT_ALL_ITEM_ID } from "app/pages/sales-intelligence/constants/common";
import { useElementSize } from "app/pages/sales-intelligence/hooks/useElementSize";

import { ScrollDismissBoundary } from "../../scroll-dismiss/ScrollDismissBoundary";
import { StyledBaseDropdownButtonWrap } from "../../styles";
import type { IGenericDropDownItem } from "../types";

const EMPTY_ITEMS: readonly IGenericDropDownItem<never>[] = [];
const EMPTY_SELECTION: readonly never[] = [] as const;

type ComboboxWithAsyncSelectAllProps<T extends string | number> = {
    items: readonly IGenericDropDownItem<T>[];
    selectedItems: readonly T[] | undefined;
    selectAllLabel: string;
    buttonText: string;
    searchPlaceholder?: string;
    isLoading?: boolean;
    searchTerm: string;
    hideArrow?: boolean;
    disabled?: boolean;
    dataAutomation?: string;
    withEmptyState?: boolean;
    onSearchChange: (value: string) => void;
    onSelectionChange: (items: readonly T[], isAllSelected: boolean) => void;
};

const ComboboxWithAsyncSelectAll = <T extends string | number>({
    items = EMPTY_ITEMS as readonly IGenericDropDownItem<T>[],
    selectedItems = EMPTY_SELECTION as readonly T[],
    onSelectionChange,
    selectAllLabel,
    buttonText,
    searchPlaceholder,
    isLoading = false,
    searchTerm,
    hideArrow = false,
    disabled = false,
    withEmptyState = true,
    dataAutomation,
    onSearchChange,
}: ComboboxWithAsyncSelectAllProps<T>) => {
    const { translate } = useTranslate();

    const [isOpen, setIsOpen] = useState(false);

    if (disabled && isOpen) {
        setIsOpen(false);
    }

    const { ref: triggerRef, size: triggerSize } = useElementSize();

    const allLoadedIds = useMemo(() => items.map((item) => item.id), [items]);
    const hasMultipleItems = allLoadedIds.length > 1;
    const isAllSelected = allLoadedIds.length > 0 && allLoadedIds.every((id) => selectedItems.includes(id));
    const isPartiallySelected = !isAllSelected && allLoadedIds.some((id) => selectedItems.includes(id));

    const handleOpenChange = useCallback(
        (open: boolean) => {
            if (disabled && open) {
                return;
            }

            setIsOpen(open);
            if (!open) {
                onSearchChange("");
            }
        },
        [disabled, onSearchChange],
    );

    const handleSelectAll = useCallback(() => {
        const next = isAllSelected
            ? selectedItems.filter((id) => !allLoadedIds.includes(id))
            : [...new Set([...selectedItems, ...allLoadedIds])];
        onSelectionChange(next, true);
    }, [isAllSelected, selectedItems, allLoadedIds, onSelectionChange]);

    const handleSelect = useCallback(
        (itemId: T) => {
            const isSelected = selectedItems.includes(itemId);
            const next = isSelected ? selectedItems.filter((id) => id !== itemId) : [...selectedItems, itemId];

            onSelectionChange(next, false);
        },
        [selectedItems, onSelectionChange],
    );

    return (
        <ScrollDismissBoundary
            isOpen={isOpen}
            onClose={() => handleOpenChange(false)}
        >
            <Combobox
                data-automation={dataAutomation}
                isOpen={isOpen}
                placement="bottom-start"
                offset={-40}
                width={triggerSize?.width ? `${triggerSize.width}px` : undefined}
                onOpenChange={handleOpenChange}
            >
                <Combobox.Trigger ref={triggerRef}>
                    <StyledBaseDropdownButtonWrap disabled={disabled}>
                        <DropdownButton
                            disabled={disabled}
                            hideArrow={hideArrow}
                        >
                            {buttonText}
                        </DropdownButton>
                    </StyledBaseDropdownButtonWrap>
                </Combobox.Trigger>

                <Combobox.SearchInput
                    value={searchTerm}
                    onChange={onSearchChange}
                    placeholder={searchPlaceholder}
                    isLoading={isLoading}
                />

                <Combobox.Content
                    emptyState={
                        withEmptyState ? (
                            <Combobox.OptionLabel>{translate("dropdown.no.results")}</Combobox.OptionLabel>
                        ) : null
                    }
                >
                    {hasMultipleItems ? (
                        <Combobox.Option
                            key={SELECT_ALL_ITEM_ID}
                            id={SELECT_ALL_ITEM_ID}
                            textValue={selectAllLabel}
                            onClick={handleSelectAll}
                        >
                            <Flex
                                alignItems="center"
                                justifyContent="space-between"
                                gap="8px"
                                width="100%"
                            >
                                <Combobox.OptionLabel>{selectAllLabel}</Combobox.OptionLabel>
                                <Combobox.OptionCheckbox
                                    isChecked={isAllSelected}
                                    halfSelected={isPartiallySelected}
                                />
                            </Flex>
                        </Combobox.Option>
                    ) : null}

                    {items.map((item) => (
                        <Combobox.Option
                            key={String(item.id)}
                            id={String(item.id)}
                            textValue={item.text ?? ""}
                            onClick={() => handleSelect(item.id)}
                        >
                            <Flex
                                alignItems="center"
                                justifyContent="space-between"
                                gap="8px"
                                width="100%"
                            >
                                <Combobox.OptionLabel>{item.text}</Combobox.OptionLabel>
                                <Combobox.OptionCheckbox isChecked={selectedItems.includes(item.id)} />
                            </Flex>
                        </Combobox.Option>
                    ))}
                </Combobox.Content>
            </Combobox>
        </ScrollDismissBoundary>
    );
};

export default ComboboxWithAsyncSelectAll;
